// tabChannel.js - BroadcastChannel for cross-tab auth communication

/**
 * @typedef {Object} AuthMessage
 * @property {string} type - Message type (e.g. 'token-updated')
 * @property {Object} [payload] - Optional message payload
 * @property {number} timestamp - Message timestamp
 */

/**
 * Creates a BroadcastChannel for auth communication between tabs
 * @param {(message: AuthMessage) => void} onMessage - Callback for handling messages
 * @returns {BroadcastChannel|null} - Returns channel or null if not supported
 * @throws {Error} If onMessage is not a function when provided
 */
export function createAuthChannel(onMessage) {
  if (typeof BroadcastChannel === 'undefined') {
    console.warn('[tabChannel] BroadcastChannel not supported in this browser')

    return null
  }

  if (onMessage !== undefined && typeof onMessage !== 'function') {
    throw new Error('[tabChannel] onMessage must be a function')
  }

  try {
    const channel = new BroadcastChannel('auth-channel')

    if (onMessage) {
      channel.onmessage = event => {
        const message = event.data

        // Type checking
        if (!message || typeof message !== 'object') {
          console.warn('[tabChannel] Received invalid message format:', message)

          return
        }

        if (!message.type || typeof message.type !== 'string') {
          console.warn('[tabChannel] Message missing required type:', message)

          return
        }

        if (!message.timestamp || typeof message.timestamp !== 'number') {
          console.warn('[tabChannel] Message missing required timestamp:', message)

          return
        }

        console.debug('[tabChannel] Received message:', message)
        onMessage(message)
      }
    }

    return channel
  } catch (err) {
    console.error('[tabChannel] Failed to create channel:', err)

    return null
  }
}

/**
 * Send a message through the auth channel
 * @param {BroadcastChannel} channel - The broadcast channel
 * @param {string} type - Message type
 * @param {object} [payload={}] - Optional message payload
 * @throws {Error} If type is not a string or payload is not an object
 */
export function sendAuthMessage(channel, type, payload = {}) {
  if (!channel) {
    console.warn('[tabChannel] No channel available')

    return
  }

  if (typeof type !== 'string') {
    throw new Error('[tabChannel] Message type must be a string')
  }

  if (payload !== undefined && (typeof payload !== 'object' || payload === null)) {
    throw new Error('[tabChannel] Message payload must be an object')
  }

  try {
    const message = {
      type,
      payload,
      timestamp: Date.now(),
    }

    channel.postMessage(message)
    console.debug(`[tabChannel] Sent message: ${type}`, message)
  } catch (err) {
    console.error('[tabChannel] Failed to send message:', err)
    throw err // Re-throw to allow error handling by caller
  }
}

/**
 * Close the auth channel
 * @param {BroadcastChannel} channel - The broadcast channel
 */
export function closeAuthChannel(channel) {
  if (channel) {
    try {
      channel.close()
      console.debug('[tabChannel] Channel closed')
    } catch (err) {
      console.error('[tabChannel] Failed to close channel:', err)
    }
  }
}
